import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Package, Search, ChevronLeft, ChevronRight, QrCode, AlertTriangle } from 'lucide-react';
import { Button } from '../../components/common/Button';
import { QRCodeModal } from '../../components/common/QRCodeModal';
import { productsApi } from '../../services/api';
import { Product, ProductImage } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { useCursorPagination, CursorFetchResult } from '../../hooks/useCursorPagination';

interface ProductListProps {
  onNavigate: (page: string, id?: string) => void;
}

export const ProductList: React.FC<ProductListProps> = ({ onNavigate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [imageUrls, setImageUrls] = useState<Map<string, string>>(new Map());
  const [qrModalProduct, setQrModalProduct] = useState<Product | null>(null);
  const { organization } = useAuth();

  // Debounce search term to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // SC-072: the backend is forward-only (a signed, opaque cursor - see
  // square_listing_cursor.py). Cursor-stack mechanics, the error/reset
  // race-guard, and the "only advance on success" rule all live in
  // useCursorPagination; this component only supplies the concrete fetch
  // and classifies its outcome.
  const fetchPage = useCallback(async (cursor: string | undefined): Promise<CursorFetchResult<Product>> => {
    const response = await productsApi.getAll({ cursor, pageSize: 12, search: debouncedSearchTerm });

    if (response.success && response.data) {
      return { kind: 'success', data: response.data.data, nextCursor: response.data.nextCursor };
    }

    // A cursor is bound to the query it was minted for (search): only
    // classify a 400 as a rejected cursor when a cursor was actually
    // sent. A cursorless request that 400s for another reason (a bad
    // search param, validation) is a real error, never a pagination
    // hiccup - misclassifying it would silently swallow it.
    if (response.status === 400 && cursor !== undefined) {
      return { kind: 'cursorRejected' };
    }

    return { kind: 'error', message: response.message ?? 'Failed to load products.' };
  }, [debouncedSearchTerm]);

  const {
    items: products,
    loading,
    error,
    pageIndex,
    canGoNext,
    canGoPrev,
    goNext,
    goPrev,
    retry,
    refresh,
  } = useCursorPagination<Product>(fetchPage, debouncedSearchTerm);

  // Resolve image URLs for whatever page is currently on screen.
  useEffect(() => {
    setImageUrls(currentUrls => {
      const newImageUrls = new Map(currentUrls);
      const urlPromises: Promise<void>[] = [];

      products.forEach(product => {
        product.images?.forEach(image => {
          if ((image as ProductImage & { needsUrlResolution?: boolean }).needsUrlResolution && image.id && !newImageUrls.has(image.id)) {
            urlPromises.push(
              productsApi.getImageUrl(image.id).then(url => {
                if (url) {
                  setImageUrls(prevUrls => new Map(prevUrls.set(image.id, url)));
                }
              })
            );
          }
        });
      });

      // Don't change the Map if no new URLs to resolve
      if (urlPromises.length === 0) {
        return currentUrls;
      }

      return newImageUrls;
    });
  }, [products]);

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this product?')) return;

    setDeleting(id);
    setDeleteError(null);
    try {
      const response = await productsApi.delete(id);
      if (response.success) {
        // SC-078 review (Wren HIGH): reload the current page with
        // refresh(), never retry(). retry() replays the last ATTEMPT's
        // isReset flag verbatim - on page 1 that's the mount/search-reset
        // load (isReset: true) - so using it here would misclassify a
        // transient post-delete refresh failure as a failed search-reset
        // and wipe an otherwise-still-valid product list.
        await refresh();
      } else {
        // A non-thrown failure (e.g. a 4xx/5xx response the API layer
        // resolves rather than throws) was previously swallowed entirely -
        // surface it the same way as a thrown failure, consistent with the
        // SC-072 error-affordance pattern below.
        setDeleteError(response.message ?? 'Failed to delete product.');
      }
    } catch (thrown) {
      setDeleteError(thrown instanceof Error ? thrown.message : 'Failed to delete product.');
    } finally {
      setDeleting(null);
    }
  };

  // Products are already filtered by the server, no need for client-side filtering
  const filteredProducts = products;

  // Only the very first load (nothing on screen yet, no error) blanks the
  // whole page. A Next/Prev/retry fetch keeps the current page's header,
  // search box, and grid visible - see useCursorPagination's "only advance
  // on success" rule: the grid never gets out of sync with the header
  // while a page-2 request is in flight.
  if (loading && products.length === 0 && !error) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Products</h2>
          {/* SC-072: the backend's `total` is cumulative-delivered-so-far
              across a paged walk, not an upfront grand total (SC-069/PR
              #73) - it is never rendered as an inventory count here. */}
          <p className="text-gray-600">Manage your product inventory</p>
        </div>
        <Button
          onClick={() => onNavigate('product-add-edit')}
          icon={Plus}
          size="lg"
        >
          Add Product
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="text"
          placeholder="Search products by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {loading && debouncedSearchTerm && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          </div>
        )}
      </div>

      {/* Delete-failure affordance: reuses the SC-072 inline error-banner
          pattern (role="alert", AlertTriangle, red border/background)
          rather than inventing a new one. A failed delete no longer
          disappears into console.error with nothing shown to the user. */}
      {deleteError && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
        >
          <div className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">{deleteError}</span>
          </div>
          <Button onClick={() => setDeleteError(null)} variant="outline" size="sm">
            Dismiss
          </Button>
        </div>
      )}

      {/* Products Grid: error / empty / loaded are three distinct states
          (js-ts.md rule 32). A load failure is never rendered as an empty
          catalog - a user who can't see products because of a backend
          error must not be told "no products yet" and steered toward
          creating a duplicate. The empty-catalog CTA below renders only
          on a confirmed successful empty result.

          A failure is only ever full-page when nothing has EVER loaded
          (the true initial-load-failed case). Once a page has loaded
          successfully at least once, useCursorPagination's "only advance
          on success" rule (fix 4) guarantees `products` still holds that
          last-good page when a later Next/Prev/retry fails - so a
          transient failure renders as a non-destructive banner above the
          still-visible grid and pagination footer, not a full replacement
          that strips Prev/Next and strands the user. */}
      {error && products.length === 0 ? (
        <div className="text-center py-12" role="alert">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Unable to load products
          </h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => void retry()} variant="outline">
            Retry
          </Button>
        </div>
      ) : (
        <>
          {error && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            >
              <div className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
              <Button onClick={() => void retry()} variant="outline" size="sm">
                Retry
              </Button>
            </div>
          )}

          {filteredProducts.length === 0 ? (
            <div className="text-center py-12">
              <Package className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {debouncedSearchTerm ? 'No products found' : 'No products yet'}
              </h3>
              <p className="text-gray-600 mb-6">
                {debouncedSearchTerm
                  ? `No products found matching "${debouncedSearchTerm}". Try a different search term.`
                  : 'Get started by adding your first product'
                }
              </p>
              {!debouncedSearchTerm && (
                <div className="flex gap-3 justify-center">
                  <Button
                    onClick={() => onNavigate('product-add-edit')}
                    icon={Plus}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Add with AI
                  </Button>
                  <Button
                    onClick={() => onNavigate('product-add-edit')}
                    icon={Plus}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Add Manually
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredProducts.map((product) => (
                  <div
                    key={product.id}
                    role="button"
                    tabIndex={0}
                    className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden cursor-pointer"
                    onClick={() => onNavigate('product-view', product.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onNavigate('product-view', product.id);
                      }
                    }}
                  >
                    {/* Product Image */}
                    <div className="aspect-square bg-gray-100 relative">
                      {(() => {
                        const firstImage = product.images && product.images.length > 0 ? product.images[0] : null;
                        // Prefer the url property if it exists, only use imageUrls map as fallback
                        const resolvedUrl = firstImage?.url || (firstImage?.id ? imageUrls.get(firstImage.id) : undefined);

                        if (resolvedUrl) {
                          return (
                            <img
                              src={resolvedUrl}
                              alt={product.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                // Show fallback icon when image fails to load
                                (e.target as HTMLImageElement).style.display = 'none';
                                const parent = (e.target as HTMLImageElement).parentElement;
                                if (parent && !parent.querySelector('.fallback-icon')) {
                                  const fallback = document.createElement('div');
                                  fallback.className = 'fallback-icon w-full h-full flex items-center justify-center';
                                  fallback.innerHTML = '<div class="text-center"><svg class="w-12 h-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg><div class="text-xs text-gray-500">Image load failed</div></div>';
                                  parent.appendChild(fallback);
                                }
                              }}
                            />
                          );
                        } else if (firstImage && (firstImage as ProductImage & { needsUrlResolution?: boolean }).needsUrlResolution) {
                          // Show loading placeholder while URL is being resolved
                          return (
                            <div className="w-full h-full flex items-center justify-center">
                              <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                                <div className="text-xs text-gray-500">Loading image...</div>
                              </div>
                            </div>
                          );
                        } else {
                          // No images available
                          return (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-12 h-12 text-gray-400" />
                            </div>
                          );
                        }
                      })()}
    
                      {/* Action Buttons */}
                      <div className="absolute top-2 right-2 flex space-x-1">
                        {/* QR Code Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setQrModalProduct(product);
                          }}
                          className="bg-white/90 backdrop-blur-sm text-gray-600 hover:text-green-600 p-1.5 rounded-full transition-colors shadow-sm"
                          title="Generate QR Code"
                        >
                          <QrCode className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onNavigate('product-add-edit', product.id);
                          }}
                          className="bg-white/90 backdrop-blur-sm text-gray-600 hover:text-blue-600 p-1.5 rounded-full transition-colors shadow-sm"
                          title="Edit Product"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(product.id);
                          }}
                          disabled={deleting === product.id}
                          className="bg-white/90 backdrop-blur-sm text-gray-600 hover:text-red-600 p-1.5 rounded-full transition-colors shadow-sm disabled:opacity-50"
                          title="Delete Product"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
    
                    {/* Product Info */}
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2">
                        {product.name}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">SKU: {product.sku}</p>
    
                      {/* Tags */}
                      {product.tags && product.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {product.tags.slice(0, 3).map((tag: string, index: number) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {tag}
                            </span>
                          ))}
                          {product.tags.length > 3 && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              +{product.tags.length - 3}
                            </span>
                          )}
                        </div>
                      )}
    
                      {/* Attributes Preview */}
                      <div className="text-xs text-gray-500">
                        {product.attributes && product.attributes.length > 0 && (
                          <p>{product.attributes.length} attribute{product.attributes.length !== 1 ? 's' : ''}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination: SC-072 cursor-based paging. The backend is
                  forward-only, so there is no server-side total page
                  count - controls show only the current page number, and
                  Next is disabled once `next_cursor` comes back null.
                  Stays visible and operable even while `error` is set
                  above (a failed Next/Prev must not strand the user
                  without a way to go back or move on). */}
              {(canGoPrev || canGoNext) && (
                <div className="flex items-center justify-between pt-6">
                  <div className="text-sm text-gray-600">
                    Page {pageIndex + 1}
                    {debouncedSearchTerm && (
                      <span className="ml-2">
                        (searching for "{debouncedSearchTerm}")
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void goPrev()}
                      disabled={!canGoPrev || loading}
                      icon={ChevronLeft}
                      iconPosition="left"
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void goNext()}
                      disabled={!canGoNext || loading}
                      icon={ChevronRight}
                      iconPosition="right"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* QR Code Modal */}
      {qrModalProduct && organization && (
        <QRCodeModal
          isOpen={true}
          onClose={() => setQrModalProduct(null)}
          productName={qrModalProduct.name}
          tenantSlug={organization.slug || organization.name}
          squareItemId={qrModalProduct.id}
          organizationId={organization.id}
        />
      )}
    </div>
  );
};